import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, MapPin, Check, X, ArrowLeft, Phone, Star, Loader2, PenLine } from "lucide-react";
import { COMPANY, REVIEWS_SUMMARY, TESTIMONIALS } from "../data/site";
import { usePackage, usePackages } from "../hooks/usePackages";
import { Reveal } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { PackageCard } from "../components/PackageCard";
import { Gallery } from "../components/Gallery";

const fmt = (n) => "₹" + n.toLocaleString("en-IN");

export default function PackageDetail() {
  const { slug } = useParams();
  const { data: pkg, isLoading, isError } = usePackage(slug);
  const { data: allPkgs = [] } = usePackages();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={40} /></div>;
  }
  if (isError || !pkg) return <Navigate to="/domestic-tours" replace />;

  const related = allPkgs.filter((p) => p.type === pkg.type && p.slug !== pkg.slug).slice(0, 3);
  const gallery = [pkg.image, ...(pkg.images || [])].filter(Boolean);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] flex items-end overflow-hidden">
        <Gallery images={gallery} alt={pkg.title} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A221C] via-[#0A221C]/50 to-[#0A221C]/20 pointer-events-none" />
        <div className="ll-container relative pb-14 pointer-events-none">
          <Link to={`/${pkg.type}-tours`} className="inline-flex items-center gap-2 text-white/80 hover:text-[#4ef72d] text-sm mb-5 pointer-events-auto"><ArrowLeft size={16} /> Back to {pkg.type} tours</Link>
          <div className="flex items-center gap-4 text-[#4ef72d] mb-3">
            <span className="eyebrow">{pkg.region}</span>
            <span className="flex items-center gap-1.5 text-white/80 text-sm"><Clock size={15} /> {pkg.nights}N / {pkg.days}D</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold text-white leading-none max-w-4xl">{pkg.title}</h1>
          <p className="text-white/85 text-lg mt-3">{pkg.subtitle}</p>
        </div>
      </section>

      <section className="ll-container py-16 grid lg:grid-cols-3 gap-14">
        {/* Left content */}
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-xl text-[#0A221C] leading-relaxed">{pkg.summary}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {pkg.highlights.map((h) => (
                <span key={h} className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#4ef72d]/12 text-[#0A221C] text-sm font-medium">
                  <Star size={14} className="text-[#f7ad2d] fill-[#f7ad2d]" /> {h}
                </span>
              ))}
            </div>
          </Reveal>

          {/* Itinerary */}
          <Reveal className="mt-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">Day-by-Day Itinerary</h2>
            <div className="relative border-l-2 border-[#4ef72d]/30 pl-8 space-y-8" data-testid="itinerary">
              {pkg.itinerary.map((d) => (
                <div key={d.day} className="relative">
                  <span className="absolute -left-[42px] top-0 w-6 h-6 rounded-full bg-[#4ef72d] text-[#0A221C] text-xs font-bold flex items-center justify-center">{d.day}</span>
                  <h3 className="font-display text-xl font-bold text-[#0A221C]">Day {d.day}: {d.title}</h3>
                  <p className="text-[#4A5D58] mt-1.5 leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Inclusions / Exclusions */}
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <Reveal>
              <div className="ll-card p-8">
                <h3 className="font-display text-2xl font-bold mb-5">Inclusions</h3>
                <ul className="space-y-3">
                  {pkg.inclusions.map((i) => (
                    <li key={i} className="flex gap-3 text-[#4A5D58]"><Check size={18} className="text-[#4ef72d] shrink-0 mt-0.5" /> {i}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="ll-card p-8">
                <h3 className="font-display text-2xl font-bold mb-5">Exclusions</h3>
                <ul className="space-y-3">
                  {pkg.exclusions.map((i) => (
                    <li key={i} className="flex gap-3 text-[#4A5D58]"><X size={18} className="text-[#f7ad2d] shrink-0 mt-0.5" /> {i}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Pricing table */}
          <Reveal className="mt-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Pricing</h2>
            <div className="ll-card overflow-hidden">
              <table className="w-full text-left" data-testid="pricing-table">
                <thead className="bg-[#0A221C] text-white">
                  <tr><th className="p-5 font-semibold">Occupancy</th><th className="p-5 font-semibold">Price / Person</th></tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  <tr><td className="p-5 text-[#4A5D58]">Double Sharing</td><td className="p-5 font-display text-xl font-bold">{fmt(pkg.price)}</td></tr>
                  <tr><td className="p-5 text-[#4A5D58]">Triple Sharing</td><td className="p-5 font-display text-xl font-bold">{fmt(Math.round(pkg.price * 0.92))}</td></tr>
                  <tr><td className="p-5 text-[#4A5D58]">Single Supplement</td><td className="p-5 font-display text-xl font-bold">+ {fmt(Math.round(pkg.price * 0.35))}</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#4A5D58] mt-3">* Indicative pricing. Final quote depends on travel dates, group size and hotel category.</p>
          </Reveal>
        </div>

        {/* Sticky enquiry */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28">
            <div className="ll-card p-7">
              <p className="text-sm text-[#4A5D58]">Starting from</p>
              <p className="font-display text-4xl font-bold text-[#0A221C] mb-1">{fmt(pkg.price)}</p>
              <p className="text-sm text-[#4A5D58] flex items-center gap-1.5 mb-6"><MapPin size={14} /> per person, {pkg.nights}N/{pkg.days}D</p>
              <EnquiryForm packageName={pkg.title} compact />
              <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="btn-outline w-full mt-4"><Phone size={16} /> Call an Expert</a>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-[#0A221C] relative overflow-hidden">
        <div className="grain" />
        <div className="ll-container py-20 relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="eyebrow text-[#4ef72d]">Traveller Reviews</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">Rated {REVIEWS_SUMMARY.rating} by our travellers</h2>
              <div className="flex items-center gap-3 mt-4">
                <span className="flex gap-0.5">{[0,1,2,3,4].map((i) => <Star key={i} size={18} className="text-[#f7ad2d] fill-[#f7ad2d]" />)}</span>
                <span className="text-white/70 text-sm">{REVIEWS_SUMMARY.count}+ {REVIEWS_SUMMARY.source}</span>
              </div>
            </div>
            <a href={REVIEWS_SUMMARY.writeUrl} target="_blank" rel="noreferrer" data-testid="detail-write-review" className="btn-primary shrink-0"><PenLine size={16} /> Write a Review</a>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t) => (
              <div key={t.name} className="border border-white/10 rounded-3xl p-7">
                <div className="flex gap-1 mb-3">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={15} className="text-[#f7ad2d] fill-[#f7ad2d]" />)}</div>
                <p className="text-white/85 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <p className="mt-4 font-semibold text-white text-sm">{t.name}</p>
                <p className="text-white/50 text-xs">{t.trip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="ll-container py-24">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-10">You may also like</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {related.map((p) => <PackageCard key={p.slug} pkg={p} />)}
        </div>
      </section>
    </div>
  );
}
